<template>
  <div
    v-if="isMounted"
    class="ui-side-menu"
    :style="menuStyle"
    :class="{
      'front': !isManagement, 'management': isManagement,
      'ui-side-menu-closed': !isActive,
      'ui-side-menu-open': isActive,
    }"
  >
    <div class="facet-open-button" :style="buttonStyle">
      <button
        class="closure-button"
        @click.stop="isActive = !isActive"
        :class="{ 'is-active': !isActive, 'jps-menu-open-button': isManagement }"
        :aria-hidden="true"
      >
        <BaseIcon
          :name="isManagement ? 'mdi/menu' : isActive ? 'mdi/menu-left' : 'mdi/menu-right'"
          :alt="ls(dapLangGlobal.buttonClosure)"
        />
      </button>
      <h3 v-if="isActive && $slots.title">
        <slot name="title" />
      </h3>
    </div>
    <div class="menu-container" v-show="isActive">
      <div class="jps-side-menu-contents-body">
        <slot />
      </div>
      <div v-if="$slots.footer" class="jps-side-menu-contents-footer">
        <slot name="footer" />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { dapLangGlobal } from '#imports'

const { ls } = useI18n()
// TODO: 画面サイズの比較値を 767px で判定しており、CSS と設計が一致しないため一時的にコメントアウト
// const { $isMobile } = useNuxtApp()

// TODO: useNuxtApp() の $isMobile の判定が CSS 設計と差異があるため、暫定でベタ書き判定する。
const checkMobileSize = () => {
  return window.innerWidth <= 768
}
const updateIsMobile = () => {
  $isMobile.value = checkMobileSize()
}
onMounted(() => {
  updateIsMobile()
  window.addEventListener('resize', updateIsMobile)
})
onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobile)
})
const $isMobile = ref(false)

interface Props {
  width?: string,
  isManagement?: boolean,
  isClicked?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  width: '18rem',
  isManagement: false,
})

const isActive: Ref<boolean> = ref(true)
onBeforeMount(() => {
  isActive.value = !$isMobile.value
})

// Hydration Mismatch を回避する対応
const isMounted = ref(false)
onMounted(() => isMounted.value = true)

const menuStyle = computed(() => {
  if ($isMobile.value) {
    return {}
  } else {
    return {
      'min-width': isActive.value ? props.width : '40px',
      'max-width': props.width,
    }
  }
})

const buttonStyle = computed(() => {
  if ($isMobile.value) {
    return {}
  } else {
    return {
      width: isActive.value ? `calc(${props.width} - 1rem)` : '60px',
    }
  }
})

const isClickedValue = computed(() => props.isClicked)
const route = useRoute()
const closeSidebar = () => {
  if ($isMobile.value) {
    isActive.value = false
  }
}
watch(() => [
  isClickedValue.value,
  route.fullPath,
], closeSidebar)

const handleMenuDisplayOnResize = () => {
  const isMobileSize = $isMobile.value
  isActive.value = !isMobileSize
}
watch(() => $isMobile.value, handleMenuDisplayOnResize)
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.ui-side-menu {
  position: relative;
  transition: $transition-timing;

  .closure-button {
    position: absolute;
    display: flex;
    align-items: center;
    z-index: 100;
  }

  &.front {
    padding-top: 2rem;
    overflow-y: auto;
    scrollbar-width: thin;
    overflow-x: hidden;
    @include custom-scroll-bar();

    .closure-button {
      @include no-decorative-button;
      background-color: $white;
      color: $jps-gray-500;
      right: 0;
      top: 10px;
      height: 2.427rem;
      width: 1.5rem;
      border-top-left-radius: 6px;
      border-bottom-left-radius: 6px;
      border-top: $basic-border;
      border-left: $basic-border;
      border-bottom: $basic-border;
    }

    .menu-container {
      display: flex;
      flex-direction: column;
      min-height: 100%;
      justify-content: space-between;
    }

    .jps-side-menu-contents-body {
      border-bottom: $basic-border;
    }

    .side-menu-item {
      a {
        padding: 0 20px;
        font-size: 0.875rem;
        font-weight: bold;
        line-height: 150%;
        color: $jps-gray-800 !important;
        height: 3rem;
        display: flex;
        align-items: center;
      }
      &:not(:last-child) {
        border-bottom: $basic-border;
      }
      @include is-mobile() {
        > * {
          padding-right: 1.5rem;
        }
      }
    }
  }

  &.management {
    .facet-open-button {
      display: flex;
      align-items: center;
      margin-top: 1rem;
      min-height: 3rem;

      h3 {
        font-size: 0.875rem;
        flex-grow: 1;
        margin: {
          left: 4.5rem;
          top: 0;
          bottom: 0;
        }
      }

      h2 {
        margin: {
          top: 0;
          bottom: 0;
        }
      }
    }

    .closure-button {
      position: absolute;
      left: 1rem;
      top: 1rem;
      overflow: hidden;
      height: 3rem;
      min-width: 3rem;
    }

    &.ui-side-menu-closed {
      width: 40px;
      .closure-button {
        color: $jps-gray-800;
        background-color: $jps-gray-100;
        border-color: $jps-gray-900;
      }
    }

    .menu-container {
      height: calc($partial-scroll-height - 5rem);
      margin-top: 0rem;
      display: flex;
      flex-direction: column;
      padding-left: 1rem;

      .jps-side-menu-contents-body {
        overflow-y: auto;
        flex-grow: 1;
      }
    }
  }

  @include is-mobile() {
    position: absolute;
    min-width: 0;
    transition: width $transition-timing;
    top: calc($header-height-mobile + 2px);
    left: 0;
    z-index: 1200;

    &.front {
      width: calc(100% - 1rem);
      height: calc(100% - 1rem - #{$header-height-mobile});
      padding-top: 0;
      border-bottom-right-radius: 6px;
      box-shadow: $component-shadow;
      border: $basic-border;
      background: $jps-gray-50;

      .closure-button {
        position: fixed;
        right: 1rem;
        top: 7rem;
      }

      &.ui-side-menu-closed {
        width: 0;
        box-shadow: none;
        border: none;
        .closure-button {
          position: fixed;
          left: 0;
          border-top-left-radius: 0px;
          border-bottom-left-radius: 0px;
          border-top-right-radius: 6px;
          border-bottom-right-radius: 6px;
          border-left: none;
          border-right: $basic-border;
          z-index: 1250;
        }
      }
    }

    &.management {
      width: 95%;
      height: calc(95% - #{$header-height-mobile});
      border-bottom-right-radius: 4px;
      border: {
        style: solid;
        color: $jps-gray-300;
        width: 1px 1px 1px 0;
      }
      background: $jps-gray-100;
      padding-top: 0.25rem;

      &.ui-side-menu-closed {
        width: 0;
        border: none;
      }

      .facet-open-button {
        left: 0;
        top: 0;
        height: auto;

        .closure-button {
          border-radius: 0;

          .ui-base-icon {
            margin: 0 !important;
          }
        }
      }
    }
  }

}
</style>
